import { SyncExternalStore } from '@egr/xbox/utils/SyncExternalStore';

type StepKey = 'download' | 'initBabylon' | 'loadPec' | 'loadPbox' | 'loadGlb' | 'loadView';
type StepStatus = 'pending' | 'running' | 'done' | 'error';

export interface LoadingStep {
    key: StepKey;
    label: string;
    status: StepStatus;
    details?: string;
}
export interface EstimatedTime {
    seconds: number;
    minutes: number;
}

export interface LoadingProgressState {
    active: boolean;
    steps: Array<LoadingStep>;
    targetPercent?: number;
    estimatedTime?: EstimatedTime;
    error?: string;
}

export class LoadingProgressStore extends SyncExternalStore<LoadingProgressState> {
    constructor() {
        super({
            active: false,
            targetPercent: 0,
            steps: [],
        });
    }

    public start(steps: Array<Pick<LoadingStep, 'key' | 'label'>>) {
        const stepsArr: Array<LoadingStep> = steps.map((s): LoadingStep => ({ ...s, status: 'pending' }));
        this.updateState({ active: true, steps: stepsArr, error: undefined, estimatedTime: undefined });
    }

    public beginStep(key: StepKey) {
        const steps: Array<LoadingStep> = this.snapshot.steps.map((s) => (s.key === key ? { ...s, status: 'running' } : s));
        this.updateState({
            steps,
            estimatedTime: key === 'download' ? this.snapshot.estimatedTime : undefined,
        });
    }

    public setEstimatedTime(time: EstimatedTime | undefined, key: StepKey) {
        if (key === 'download') {
            this.updateState({
                estimatedTime: time,
            });
        }
    }
    public setTargetPercent(percent: number) {
        const percentage = Math.max(0, Math.min(100, Math.round(percent)));

        this.updateState({
            targetPercent: percentage,
        });
    }

    public completeStep(key: StepKey) {
        const steps: Array<LoadingStep> = this.snapshot.steps.map((s) => (s.key === key ? { ...s, status: 'done' } : s));
        this.updateState({
            steps,
            targetPercent: this.snapshot.targetPercent,
            estimatedTime: undefined,
        });
    }

    public failStep(key: StepKey, error: Error) {
        const steps: Array<LoadingStep> = this.snapshot.steps.map((s) => (s.key === key ? { ...s, status: 'error', details: error.message } : s));
        this.updateState({
            active: false,
            steps,
            error: error.message,
        });
    }

    public finish() {
        const steps: Array<LoadingStep> = this.snapshot.steps.map((s) => (s.status === 'done' ? s : { ...s, status: 'done' }));
        this.updateState({
            active: false,
            steps,
        });
    }
    public reset() {
        this.updateState({
            active: false,
            steps: [],
            error: undefined,
        });
    }
    public getActive() {
        return this.snapshot.active;
    }
    public getSteps() {
        return this.snapshot.steps;
    }
}

export const loadingProgressStore = new LoadingProgressStore();
